import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Container, Card, Table, Form, Button, Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { API_BASE } from "../../services/apirest";

export default function GestionBodegas() {
  const [bodegas, setBodegas] = useState([]);
  const [distribuidores, setDistribuidores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState({
    id_bodega: null,
    distribuidor_id: "",
    DISTRIBUIDOR: "",
    BODEGA: "",
    ALMACEN: "",
    REGION: "",
    DIRECCION: ""
  });

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  }), []);

  const obtenerDatos = useCallback(async () => {
    try {
      setLoading(true);
      // 1) Obtener bodegas (Global)
      const resB = await fetch(`${API_BASE}/bodegas.php`, { headers });
      const dataB = await resB.json();
      if (dataB.status === "success" || dataB.status === "ok") {
        setBodegas(dataB.result?.bodegas || []);
      }

      // 2) Obtener distribuidores para el selector
      const resD = await fetch(`${API_BASE}/distribuidores.php`, { headers });
      const dataD = await resD.json();
      if (dataD.status === "success" || dataD.status === "ok") {
        setDistribuidores(dataD.result?.distribuidores || []);
      }
    } catch (err) {
      console.error("Error al obtener datos:", err);
      Swal.fire("Error", "No se pudo conectar con el servidor", "error");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    obtenerDatos();
  }, [obtenerDatos]);

  const filteredBodegas = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return bodegas;
    return bodegas.filter(b => 
      (b.BODEGA || "").toLowerCase().includes(term) ||
      (b.ALMACEN || "").toLowerCase().includes(term) ||
      (b.DISTRIBUIDOR || "").toLowerCase().includes(term) ||
      (b.REGION || "").toLowerCase().includes(term) ||
      (b.DIRECCION || "").toLowerCase().includes(term)
    );
  }, [bodegas, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredBodegas.length / pageSize));
  const pageItems = filteredBodegas.slice((page - 1) * pageSize, page * pageSize);

  const handleNuevo = () => {
    setFormData({
      id_bodega: null,
      distribuidor_id: "",
      DISTRIBUIDOR: "",
      BODEGA: "",
      ALMACEN: "",
      REGION: "",
      DIRECCION: ""
    });
    setModalVisible(true);
  };

  const handleEditar = (b) => {
    setFormData({
      id_bodega: b.id_bodega,
      distribuidor_id: b.distribuidor_id,
      DISTRIBUIDOR: b.DISTRIBUIDOR,
      BODEGA: b.BODEGA,
      ALMACEN: b.ALMACEN,
      REGION: b.REGION,
      DIRECCION: b.DIRECCION
    });
    setModalVisible(true);
  };

  const handleEliminar = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE}/bodegas.php`, {
          method: "DELETE",
          headers,
          body: JSON.stringify({ id_bodega: id })
        });
        const data = await res.json();
        if (data.status === "success" || data.status === "ok") {
          Swal.fire("Eliminado", "La bodega ha sido eliminada.", "success");
          obtenerDatos();
        } else {
          Swal.fire("Error", data.result?.error_msg || "No se pudo eliminar", "error");
        }
      } catch (err) {
        Swal.fire("Error", "Error de conexión", "error");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.distribuidor_id || !formData.BODEGA || !formData.ALMACEN) {
      Swal.fire("Cuidado", "Por favor completa los campos obligatorios.", "warning");
      return;
    }

    // El backend necesita DISTRIBUIDOR (string)
    const distName = distribuidores.find(d => String(d.id_distribuidor) === String(formData.distribuidor_id))?.distribuidores || "";
    const payload = { ...formData, DISTRIBUIDOR: distName };

    try {
      const method = formData.id_bodega ? "PUT" : "POST";
      const res = await fetch(`${API_BASE}/bodegas.php`, {
        method,
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.status === "success" || data.status === "ok") {
        Swal.fire("Listo", formData.id_bodega ? "Bodega actualizada" : "Bodega creada", "success");
        setModalVisible(false);
        obtenerDatos();
      } else {
        Swal.fire("Error", data.result?.error_msg || "Ocurrió un error", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Error de conexión", "error");
    }
  };

  const handleExportar = () => {
    if (bodegas.length === 0) {
      Swal.fire("Aviso", "No hay datos para exportar", "info");
      return;
    }

    const dataToExport = filteredBodegas.map((b) => ({
      DISTRIBUIDOR: b.DISTRIBUIDOR,
      REGION: b.REGION,
      BODEGA: b.BODEGA,
      ALMACEN: b.ALMACEN,
      DIRECCION: b.DIRECCION,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bodegas");
    XLSX.writeFile(workbook, "Listado_Bodegas.xlsx");
  };

  return (
    <Container className="my-5 pt-5 mt-5">
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Administración de Bodegas</h4>
          <div className="d-flex gap-2">
            <Button variant="outline-success" onClick={handleExportar}>
              Exportar Excel
            </Button>
            <Button variant="primary" onClick={handleNuevo}>
              Agregar Bodega
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex mb-3 gap-3">
            <Form.Control
              type="text"
              placeholder="Buscar bodega, almacén, distribuidor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: "400px" }}
            />
            <div className="ms-auto d-flex align-items-center gap-2">
              <span className="text-muted small">Filas:</span>
              <Form.Select size="sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: "80px" }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>
          </div>

          <Table responsive striped hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Distribuidor</th>
                <th>Región</th>
                <th>Bodega</th>
                <th>Almacén</th>
                <th>Dirección</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-4 text-muted">No se encontraron bodegas.</td></tr>
              ) : (
                pageItems.map(b => (
                  <tr key={b.id_bodega}>
                    <td className="small">{b.DISTRIBUIDOR}</td>
                    <td>{b.REGION}</td>
                    <td className="fw-bold">{b.BODEGA}</td>
                    <td>{b.ALMACEN}</td>
                    <td className="small">{b.DIRECCION}</td>
                    <td className="text-center">
                      <Button variant="outline-warning" size="sm" className="me-2" onClick={() => handleEditar(b)}>Editar</Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleEliminar(b.id_bodega)}>Eliminar</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3 gap-2">
              <Button size="sm" variant="light" disabled={page === 1} onClick={() => setPage(1)}>«</Button>
              <Button size="sm" variant="light" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
              <span className="align-self-center px-3">Página {page} de {totalPages}</span>
              <Button size="sm" variant="light" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
              <Button size="sm" variant="light" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={modalVisible} onHide={() => setModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{formData.id_bodega ? "Editar Bodega" : "Nueva Bodega"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Distribuidor (Obligatorio)</Form.Label>
              <Form.Select
                value={formData.distribuidor_id}
                onChange={(e) => setFormData({ ...formData, distribuidor_id: e.target.value })}
                required
              >
                <option value="">-- Selecciona un distribuidor --</option>
                {distribuidores.map(d => (
                  <option key={d.id_distribuidor} value={d.id_distribuidor}>{d.distribuidores}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Región</Form.Label>
              <Form.Control
                type="text"
                value={formData.REGION}
                onChange={(e) => setFormData({ ...formData, REGION: e.target.value.toUpperCase() })}
                style={{ textTransform: "uppercase" }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre de Bodega (Obligatorio)</Form.Label>
              <Form.Control
                type="text"
                value={formData.BODEGA}
                onChange={(e) => setFormData({ ...formData, BODEGA: e.target.value.toUpperCase() })}
                style={{ textTransform: "uppercase" }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Almacén (Obligatorio)</Form.Label>
              <Form.Control
                type="text"
                value={formData.ALMACEN}
                onChange={(e) => setFormData({ ...formData, ALMACEN: e.target.value.toUpperCase() })}
                style={{ textTransform: "uppercase" }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.DIRECCION}
                onChange={(e) => setFormData({ ...formData, DIRECCION: e.target.value.toUpperCase() })}
                style={{ textTransform: "uppercase" }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalVisible(false)}>Cancelar</Button>
            <Button variant="primary" type="submit">Guardar</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
